import { useEffect, useLayoutEffect, useRef } from 'react';

import { isMac } from '@frontend/utils/platform';

/**
 * Register the Cmd+K (mac) / Ctrl+K (win/linux) shortcut that toggles the
 * Spotlight, active only while `enabled`. The modifier is platform-split so mac's
 * native Ctrl+K (kill-to-EOL in inputs) is left alone. Intentionally has no
 * editable-target guard — the Spotlight should open from anywhere, including inputs.
 */
export function useSpotlightShortcut(enabled: boolean, onToggle: () => void): void {
  // Read onToggle through a ref so callers can pass an inline callback without
  // the effect re-subscribing the window listener on every render.
  const onToggleRef = useRef(onToggle);
  useLayoutEffect(() => {
    onToggleRef.current = onToggle;
  });

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.repeat) return; // ignore auto-repeat while the combo is held (no open/close flicker)
      if (e.key !== 'k' && e.key !== 'K') return;
      // Cmd on mac, Ctrl on win/linux — not "either", so mac's native Ctrl+K survives.
      // Require the OTHER modifiers absent so combos like Cmd+Shift+K / Cmd+Opt+K
      // (or Ctrl+Shift+K on win/linux) don't hijack native browser shortcuts.
      const primary = isMac() ? e.metaKey : e.ctrlKey;
      const others = isMac()
        ? e.ctrlKey || e.altKey || e.shiftKey
        : e.metaKey || e.altKey || e.shiftKey;
      if (!primary || others) return;
      e.preventDefault();
      onToggleRef.current();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled]);
}
